import type { QueryClient } from "@tanstack/react-query";
import {
  UserOwnViewSchema,
  type UserOwnView,
} from "@shared/schemas/user-views.schemas";
import type { CreateOAuthSessionBody } from "@shared/schemas/oauth-session.schemas";
import type { AuthSessionResult } from "@shared/schemas/authentication.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";
import type { LoginCredentials } from "./auth.api.js";
import { createAuthApi } from "./auth.api.js";
import type { AxiosInstance } from "axios";
import { isAxiosError } from "axios";
import { ActorAuthorizationErrorCodes } from "@shared/constants/actor-authorization-errors.constants";
import { extractApiErrorCode } from "@frontend/shared/infrastructure/errors/error.utils";
import { addResponseInterceptor } from "@frontend/shared/infrastructure/api/base-api-client";
import { CURRENT_USER_QUERY_KEY } from "./current-user-query-keys.js";

let registeredInterceptor: { client: AxiosInstance; id: number } | null = null;

export class CurrentUserHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createAuthApi(apiClient);
    this.clearCurrentUserOnNoSession(apiClient);
  }

  // Clears the cached user instead of navigating: host and admin mount under router basenames, so only
  // AuthenticatedRouteGuard knows which login to reach. Ejecting first keeps a dev HMR re-run of
  // configureFrontendDAL from stacking handlers.
  private clearCurrentUserOnNoSession(apiClient: AxiosInstance): void {
    if (registeredInterceptor !== null) {
      registeredInterceptor.client.interceptors.response.eject(
        registeredInterceptor.id,
      );
    }
    const id = addResponseInterceptor(
      apiClient,
      (response) => response,
      (error: unknown) => {
        if (isNoSessionError(error)) {
          this.setCachedData(CURRENT_USER_QUERY_KEY, null);
        }
        return Promise.reject(error);
      },
    );
    registeredInterceptor = { client: apiClient, id };
  }

  createHooks() {
    return {
      useCurrentUser: () => {
        return this.query<unknown, UserOwnView | null>({
          key: CURRENT_USER_QUERY_KEY,
          fn: async (): Promise<unknown> => {
            try {
              return await this.api.getCurrentUser();
            } catch (caughtError) {
              if (isNoSessionError(caughtError)) {
                applicationLogger.debug(
                  "User not authenticated (USER_ACTIONS_FORBIDDEN received)",
                  "CurrentUserHooksHttpRepository",
                );
                return null;
              }

              applicationLogger.error(
                "Failed to fetch current user",
                "CurrentUserHooksHttpRepository",
                caughtError instanceof Error ? caughtError : undefined,
              );
              throw caughtError;
            }
          },
          parse: (raw) =>
            raw === null || raw === undefined
              ? null
              : UserOwnViewSchema.parse(raw),
          staleTime: 5 * 60 * 1000, // 5 minutes
          retry: 0, // Don't retry auth failures
        });
      },

      useLogout: () => {
        return this.voidMutation<void>({
          fn: async () => {
            await this.api.logout();
          },
          onSuccess: () => {
            this.setCachedData(CURRENT_USER_QUERY_KEY, null);

            void this.queryClient.invalidateQueries();
          },
        });
      },

      useRefreshCurrentUser: (): (() => Promise<void>) => {
        return async (): Promise<void> => {
          await this.queryClient.invalidateQueries({
            queryKey: CURRENT_USER_QUERY_KEY,
          });
        };
      },

      useCreateSession: () => {
        return this.mutation<AuthSessionResult, AuthSessionResult, string>({
          fn: (token) => this.api.createSession(token),
          parse: (data) => data,
        });
      },

      useLogin: () => {
        return this.mutation<
          AuthSessionResult,
          AuthSessionResult,
          { credentials: LoginCredentials; redirectUrl?: string }
        >({
          fn: (input) => this.api.login(input.credentials, input.redirectUrl),
          parse: (data) => data,
        });
      },

      useCompleteOAuthSession: () => {
        return this.mutation<
          AuthSessionResult,
          AuthSessionResult,
          CreateOAuthSessionBody
        >({
          fn: (body) => this.api.completeOAuthSession(body),
          parse: (data) => data,
          onSuccess: () => {
            this.invalidateQueries(CURRENT_USER_QUERY_KEY);
          },
        });
      },
    };
  }
}

function isNoSessionError(error: unknown): boolean {
  return (
    isAxiosError(error) &&
    error.response?.status === 403 &&
    extractApiErrorCode(error) ===
      ActorAuthorizationErrorCodes.USER_ACTIONS_FORBIDDEN
  );
}
